import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Download, Shield, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

type ImportResult = {
  inserted: number;
  skipped: number;
  total: number;
  errors: string[];
  totalRows: number;
};

export default function AdminImport() {
  const [isPending, setIsPending] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleImport() {
    setIsPending(true);
    setResult(null);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("No active session. Please sign in again.");
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-ch-ai-companies`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
        }
      );

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody?.error || `HTTP ${response.status}`);
      }

      const data = await response.json();

      // After success, get total row count
      const { count } = await supabase
        .from("ch_ai_leads")
        .select("*", { count: "exact", head: true });

      setResult({
        inserted: data.inserted ?? 0,
        skipped: data.skipped ?? 0,
        total: data.total ?? 0,
        errors: data.errors ?? [],
        totalRows: count ?? 0,
      });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Unknown error occurred");
    } finally {
      setIsPending(false);
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-start justify-center pt-24 px-4">
      <div className="w-full max-w-lg space-y-4">
        {/* Header */}
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <Shield className="w-4 h-4 text-primary" />
          <span>Internal Admin</span>
          <span className="text-border">·</span>
          <span>AI Startup Sourcing</span>
        </div>

        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="font-display text-xl flex items-center gap-2">
              <Download className="w-5 h-5 text-primary" />
              Import AI Companies
            </CardTitle>
            <CardDescription>
              Fetches newly incorporated UK AI companies from Companies House (last 6 months) and stores new leads in the database.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              className="w-full"
              size="lg"
              onClick={handleImport}
              disabled={isPending}
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importing… this may take up to 60s
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Import AI Companies
                </>
              )}
            </Button>

            {/* Success result */}
            {result && !error && (
              <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <CheckCircle2 className="w-4 h-4 text-primary" />
                  Import complete
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="rounded-md bg-background border border-border p-3">
                    <div className="text-2xl font-bold text-primary">{result.inserted}</div>
                    <div className="text-xs text-muted-foreground mt-1">New leads added</div>
                  </div>
                  <div className="rounded-md bg-background border border-border p-3">
                    <div className="text-2xl font-bold text-muted-foreground">{result.skipped}</div>
                    <div className="text-xs text-muted-foreground mt-1">Duplicates skipped</div>
                  </div>
                  <div className="rounded-md bg-background border border-border p-3">
                    <div className="text-2xl font-bold text-foreground">{result.total}</div>
                    <div className="text-xs text-muted-foreground mt-1">Total fetched</div>
                  </div>
                </div>
                <div className="text-sm text-muted-foreground border-t border-border pt-3">
                  Total rows in database:{" "}
                  <span className="font-semibold text-foreground">{result.totalRows}</span>
                </div>
                {result.errors.length > 0 && (
                  <details className="text-xs text-muted-foreground">
                    <summary className="cursor-pointer hover:text-foreground">
                      {result.errors.length} warning(s)
                    </summary>
                    <ul className="mt-2 space-y-1 list-disc list-inside">
                      {result.errors.map((e, i) => (
                        <li key={i}>{e}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 flex items-start gap-2 text-sm text-destructive">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-xs text-center text-muted-foreground">
          <a href="/admin/ai-sourcing" className="hover:text-foreground underline">
            ← View full leads table
          </a>
        </p>
      </div>
    </div>
  );
}
